// /dealeri/:alias — public dealer summary page.
// Displays company name, city, description, and a short preview of active listings.
// Reuses site Header/Footer (from App.tsx) and SEOFooterContent at the bottom.

import { useEffect, useMemo, useState } from "react";
import { trackDealerContactClick } from '@/utils/contactTracking';
import { Link, useParams, Navigate } from "@/lib/router-compat";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import { SEOFooterContent } from "@/components/seo/SEOFooterContent";
import { ModernListingCard } from "@/components/listings/card/ModernListingCard";
import { buildListingSlug } from "@/utils/listingSlug";
import { MapPin, ArrowRight, Phone, EnvelopeSimple, Globe, WhatsappLogo, Clock, ShieldCheck, Copy, Check } from "@phosphor-icons/react";
import { toWhatsAppNumber } from "@/utils/contactInfo";
import { useRouteLoaderData } from "@/lib/seo/loader-data";
import { setAssistantDealerContext } from "@/utils/assistantContext";
import { DealerVerificationBadge } from '@/components/dealers/DealerVerificationBadge';
import { isDealerVerified } from '@/utils/dealerVerification';

interface DealerRow {
  verified?: boolean;
  id: string;
  user_id: string;
  subdomain_alias: string | null;
  company_name: string | null;
  showroom_name: string | null;
  city: string | null;
  company_description: string | null;
  logo_url: string | null;
  cover_image_url: string | null;
  verification_status: string | null;
  business_phone: string | null;
  business_phone_fix: string | null;
  business_email: string | null;
  business_address: string | null;
  website_url: string | null;
  whatsapp_enabled: boolean | null;
  opening_hours: any;
  years_in_business: number | null;
  risco_verified: boolean | null;
  risco_company_name: string | null;
  average_rating: number | null;
  total_reviews: number | null;
}

interface ListingRow {
  id: string;
  title: string | null;
  price: number | null;
  make: string | null;
  model: string | null;
  year: number | null;
  mileage_value: number | null;
  mileage_unit: string | null;
  fuel_type: string | null;
  transmission: string | null;
  location_city: string | null;
  photos: any;
  created_at: string | null;
}

const PREVIEW_LIMIT = 6;
const FETCH_LIMIT = 120;

export default function DealerPublicPage({ resolvedAlias }: { resolvedAlias?: string } = {}) {
  const { alias: routeAlias = "" } = useParams<{ alias: string }>();
  const alias = resolvedAlias ?? routeAlias;
  // Seeded from the route loader so the server HTML carries the business name,
  // location, verification details and inventory.
  const ssr = useRouteLoaderData<{ alias?: string; dealer?: DealerRow | null; listings?: ListingRow[] }>();
  const ssrDealer = ssr?.alias === alias ? (ssr?.dealer ?? null) : null;
  const [dealer, setDealer] = useState<DealerRow | null>(ssrDealer);
  const [listings, setListings] = useState<ListingRow[]>(
    ssrDealer && Array.isArray(ssr?.listings) ? ssr.listings : [],
  );
  const [selectedMake, setSelectedMake] = useState<string | null>(null);
  const [loading, setLoading] = useState(!ssrDealer);
  const [notFound, setNotFound] = useState(false);
  const [redirectAlias, setRedirectAlias] = useState<string | null>(null);
  const [copiedAddress, setCopiedAddress] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading((prev) => (dealer ? false : prev));
      setNotFound(false);
      setRedirectAlias(null);
      const { data: dRows } = await supabase.rpc("get_dealer_by_alias", { _alias: alias });
      if (cancelled) return;
      const d = Array.isArray(dRows) && dRows.length > 0 ? (dRows[0] as DealerRow) : null;
      if (!d) {
        // Legacy fallback: /dealeri/dealer-{first 8 chars of uuid} → new slug
        const legacy = alias.match(/^dealer-([a-f0-9]{8})$/i);
        if (legacy) {
          const { data: newAlias } = await supabase.rpc(
            "get_dealer_alias_by_id_prefix",
            { _prefix: legacy[1].toLowerCase() },
          );
          if (typeof newAlias === "string" && newAlias && newAlias !== alias) {
            setRedirectAlias(newAlias);
            setLoading(false);
            return;
          }
        }
        setDealer(null);
        setNotFound(true);
        setLoading(false);
        return;
      }
      setDealer({ ...d, verified: ssrDealer?.id === d.id ? ssrDealer.verified : d.verified });

      const { data: lRows } = await supabase.rpc("dealer_page_listings", {
        _alias: alias,
        _limit: FETCH_LIMIT,
        _offset: 0,
      });
      if (cancelled) return;
      setListings(Array.isArray(lRows) ? (lRows as ListingRow[]) : []);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [alias]);

  // Informează asistentul AI despre dealerul vizionat, ca să poată răspunde
  // la întrebări despre el (program, contact, stoc, verificare).
  useEffect(() => {
    if (!dealer) {
      setAssistantDealerContext(null);
      return;
    }
    const makeSet = new Set<string>();
    listings.forEach((l) => l.make && makeSet.add(l.make));
    setAssistantDealerContext({
      id: dealer.id,
      name: dealer.company_name || dealer.showroom_name || alias,
      city: dealer.city,
      address: dealer.business_address,
      phone: dealer.business_phone || dealer.business_phone_fix,
      email: dealer.business_email,
      website: dealer.website_url,
      openingHours: dealer.opening_hours ? JSON.stringify(dealer.opening_hours) : null,
      yearsInBusiness: dealer.years_in_business,
      verified: dealer.verification_status === "verified" || dealer.risco_verified === true,
      rating: dealer.average_rating,
      reviewCount: dealer.total_reviews,
      description: dealer.company_description,
      activeListings: listings.length,
      makes: Array.from(makeSet).join(", ") || null,
    });
    return () => setAssistantDealerContext(null);
  }, [dealer, listings, alias]);

  const makes = useMemo(() => {
    const counts = new Map<string, number>();
    listings.forEach((l) => {
      const m = (l.make || "").trim();
      if (!m) return;
      counts.set(m, (counts.get(m) || 0) + 1);
    });
    return Array.from(counts.entries())
      .map(([make, count]) => ({ make, count }))
      .sort((a, b) => b.count - a.count || a.make.localeCompare(b.make));
  }, [listings]);

  const minPrice = useMemo(() => {
    const prices = listings.map((l) => Number(l.price || 0)).filter((p) => p > 0);
    return prices.length ? Math.min(...prices) : null;
  }, [listings]);

  const avgYear = useMemo(() => {
    const years = listings.map((l) => Number(l.year || 0)).filter((y) => y > 1950);
    return years.length ? Math.round(years.reduce((a, b) => a + b, 0) / years.length) : null;
  }, [listings]);

  const filteredListings = useMemo(
    () => (selectedMake ? listings.filter((l) => (l.make || "").trim() === selectedMake) : listings),
    [listings, selectedMake],
  );
  const visibleListings = filteredListings.slice(0, selectedMake ? 12 : PREVIEW_LIMIT);

  if (redirectAlias) return <Navigate to={`/dealeri/${redirectAlias}`} replace />;


  const displayName = dealer?.company_name || dealer?.showroom_name || alias;
  const title = dealer ? `${displayName} | RO Auto Market` : "Dealer | RO Auto Market";
  const description = dealer?.company_description
    ? dealer.company_description.slice(0, 160)
    : `Vezi anunțurile active ale dealerului ${displayName} pe RO Auto Market${dealer?.city ? `, ${dealer.city}` : ""}.`;
  const canonical = `https://roautomarket.ro/dealeri/${alias}`;

  if (notFound) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <Helmet>
          <title>Dealer nu a fost găsit | RO Auto Market</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <h1 className="text-2xl font-serif mb-3">Dealerul nu a fost găsit</h1>
        <p className="text-muted-foreground">
          Nu am găsit un dealer cu alias-ul <code>{alias}</code>.
        </p>
        <Link to="/dealeri-verificati" className="inline-block mt-6 text-primary underline">
          Vezi toți dealerii verificați
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="profile" />
      </Helmet>

      <section className="container mx-auto px-4 py-10">
        <div className="surface-dark band-bleed -mt-10 py-5 md:py-6 flex flex-row items-center gap-4 mb-6">
          {dealer?.logo_url && (
            <img
              src={dealer.logo_url}
              alt={`Logo ${displayName}`}
              loading="lazy"
              className="w-16 h-16 md:w-24 md:h-24 rounded-2xl object-cover bg-muted shrink-0"
            />
          )}
          <div className="min-w-0">
            <h1 className="text-2xl md:text-4xl font-serif font-medium mb-1">{displayName}</h1>
            {isDealerVerified(dealer) && <DealerVerificationBadge />}
            {dealer?.city && (
              <div className="flex items-center gap-1.5 text-muted-foreground text-sm">
                <MapPin size={16} />
                <span>{dealer.city}</span>
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col md:flex-row md:items-start gap-6 mb-10">
          <div className="flex-1 min-w-0">
            {dealer?.company_description ? (
              <p className="text-base leading-relaxed text-foreground/90 max-w-3xl whitespace-pre-line">
                {dealer.company_description}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground italic">
                Acest dealer nu a completat încă o descriere.
              </p>
            )}
            {dealer && (dealer.business_phone || dealer.business_phone_fix || dealer.business_email || dealer.business_address || dealer.website_url || dealer.opening_hours) && (
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-3xl">
                {dealer.business_address && (() => {
                  const fullAddress = [dealer.business_address, dealer.city].filter(Boolean).join(", ");
                  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`;
                  return (
                    <div className="flex items-start gap-2 text-sm sm:col-span-2">
                      <MapPin size={18} className="text-primary shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="break-words">{fullAddress}</div>
                        <div className="mt-1 flex flex-wrap gap-3">
                          <a
                            href={mapsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-primary hover:underline text-xs font-medium"
                          >
                            <MapPin size={14} weight="fill" /> Vezi pe Google Maps
                          </a>
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                await navigator.clipboard.writeText(fullAddress);
                                setCopiedAddress(true);
                                setTimeout(() => setCopiedAddress(false), 2000);
                              } catch {}
                            }}
                            className="inline-flex items-center gap-1 text-muted-foreground hover:text-primary text-xs font-medium"
                          >
                            {copiedAddress ? (
                              <>
                                <Check size={14} weight="bold" /> Copiat
                              </>
                            ) : (
                              <>
                                <Copy size={14} /> Copiază adresa
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })()}
                {dealer.business_phone && (
                  <div className="flex items-center gap-2 text-sm">
                  <a href={`tel:${dealer.business_phone}`} data-seller-contact onClick={() => trackDealerContactClick(dealer.id, 'phone', 'dealer_page')} className="flex items-center gap-2 hover:text-primary">
                    <Phone size={18} className="text-primary shrink-0" />
                    <span>{dealer.business_phone}</span>
                  </a>
                    {dealer.whatsapp_enabled && (
                      <a
                        href={`https://wa.me/${toWhatsAppNumber(dealer.business_phone)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-1 inline-flex items-center gap-1 text-emerald-600 hover:underline"
                        data-seller-contact
                        onClick={(e) => { e.stopPropagation(); trackDealerContactClick(dealer.id, 'whatsapp', 'dealer_page'); }}
                      >
                        <WhatsappLogo size={16} weight="fill" /> WhatsApp
                      </a>
                    )}
                  </div>
                )}
                {dealer.business_phone_fix && (
                  <a href={`tel:${dealer.business_phone_fix}`} data-seller-contact onClick={() => trackDealerContactClick(dealer.id, 'phone', 'dealer_page_fix')} className="flex items-center gap-2 text-sm hover:text-primary">
                    <Phone size={18} className="text-primary shrink-0" />
                    <span>{dealer.business_phone_fix} <span className="text-muted-foreground">(fix)</span></span>
                  </a>
                )}
                {dealer.business_email && (
                  <a href={`mailto:${dealer.business_email}`} className="flex items-center gap-2 text-sm hover:text-primary break-all">
                    <EnvelopeSimple size={18} className="text-primary shrink-0" />
                    <span>{dealer.business_email}</span>
                  </a>
                )}
                {dealer.website_url && (
                  <a href={dealer.website_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm hover:text-primary break-all">
                    <Globe size={18} className="text-primary shrink-0" />
                    <span>{dealer.website_url.replace(/^https?:\/\//, "")}</span>
                  </a>
                )}
                {dealer.risco_verified && (
                  <div className="flex items-center gap-2 text-sm text-emerald-700">
                    <ShieldCheck size={18} weight="fill" className="shrink-0" />
                    <span>Firmă verificată RISCO{dealer.risco_company_name ? ` — ${dealer.risco_company_name}` : ""}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Stats bar */}
        {!loading && listings.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            {[
              { label: "Anunțuri active", value: String(listings.length) },
              {
                label: "Preț de la",
                value: minPrice ? `${minPrice.toLocaleString("ro-RO")} €` : "—",
              },
              { label: "Mărci în stoc", value: String(makes.length) },
              {
                label: dealer?.total_reviews ? "Recenzii" : "An mediu",
                value: dealer?.total_reviews
                  ? `${(dealer.average_rating ?? 0).toFixed(1)}★ (${dealer.total_reviews})`
                  : avgYear
                    ? String(avgYear)
                    : "—",
              },
            ].map((s) => (
              <div key={s.label} className="rounded-xl border border-border bg-card px-4 py-3">
                <div className="text-lg md:text-xl font-semibold text-foreground">{s.value}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-baseline justify-between mb-4">
          <h2 className="text-xl md:text-2xl font-serif">Anunțuri active</h2>
          {listings.length > 0 && (
            <Link
              to={`/dealeri/${alias}/anunturi`}
              className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
            >
              Vezi toate <ArrowRight size={14} />
            </Link>
          )}
        </div>

        {/* Make filters */}
        {!loading && makes.length > 1 && (
          <div className="flex flex-wrap gap-2 mb-5">
            <button
              type="button"
              onClick={() => setSelectedMake(null)}
              className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                selectedMake === null
                  ? "bg-primary text-primary-foreground border-primary"
                  : "border-border text-muted-foreground hover:border-primary/50 hover:text-foreground"
              }`}
            >
              Toate ({listings.length})
            </button>
            {makes.map((m) => (
              <button
                key={m.make}
                type="button"
                onClick={() => setSelectedMake(m.make)}
                className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                  selectedMake === m.make
                    ? "bg-primary text-primary-foreground border-primary"
                    : "border-border text-muted-foreground hover:border-primary/50 hover:text-foreground"
                }`}
              >
                {m.make} ({m.count})
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-72 rounded-xl bg-muted animate-pulse" />
            ))}
          </div>
        ) : listings.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            Acest dealer nu are anunțuri active momentan.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {visibleListings.map((l) => {
              const photos = Array.isArray(l.photos) ? l.photos : [];
              const firstPhoto = typeof photos[0] === "string" ? photos[0] : photos[0]?.url;
              const slug = buildListingSlug({
                id: l.id,
                make: l.make,
                model: l.model,
                year: l.year ?? undefined,
                location_city: l.location_city,
              });
              const mileageKm =
                l.mileage_unit === "mi" && l.mileage_value
                  ? Math.round(Number(l.mileage_value) * 1.609)
                  : Number(l.mileage_value || 0);
              return (
                <ModernListingCard
                  key={l.id}
                  id={l.id}
                  title={l.title || `${l.make ?? ""} ${l.model ?? ""}`.trim()}
                  price={Number(l.price || 0)}
                  photo={firstPhoto}
                  images={photos.filter((p: any) => typeof p === "string") as string[]}
                  year={Number(l.year || 0)}
                  mileageKm={mileageKm}
                  fuel_type={l.fuel_type ?? undefined}
                  transmission={l.transmission ?? undefined}
                  location={l.location_city || ""}
                  location_city={l.location_city ?? undefined}
                  slug={slug}
                  make={l.make ?? undefined}
                  model={l.model ?? undefined}
                  created_at={l.created_at ?? undefined}
                />
              );
            })}
          </div>
        )}

        {listings.length >= PREVIEW_LIMIT && (
          <div className="mt-8 text-center">
            <Link
              to={`/dealeri/${alias}/anunturi`}
              className="inline-block px-6 py-3 rounded-full bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90"
            >
              Vezi toate anunțurile
            </Link>
          </div>
        )}
      </section>

      <SEOFooterContent />
    </div>
  );
}
